import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * Les cinq catégories de `Client.category` (M10). Elles viennent du **prototype validé**, pas du
 * vocabulaire de GHP — d'où le `🟡` de la fiche et les questions `Q12`/`Q13`. Tant qu'elles ne
 * sont pas confirmées, elles restent une énumération dure parce que le code branche dessus : le
 * répertoire les range, les compte et les regroupe.
 */
export const CLIENT_CATEGORIES = [
    'municipality',
    'developer',
    'social_landlord',
    'building_manager',
    'individual',
];
export const clientCategorySchema = z.enum(CLIENT_CATEGORIES);
/**
 * `Client` — **le donneur d'ordre qui paie, et lui seul** (décision du 11/09/2026). C'est le sens
 * le plus étroit du mot, donc celui qu'on peut élargir sans rien casser : le jour où GHP dira que
 * « maître d'ouvrage » est distinct du donneur d'ordre, un champ de rôle s'ajoutera sur le lien
 * demande ↔ client, sans toucher à cette forme.
 *
 * ⚠️ **Un particulier est un `Client` comme un autre**, catégorie `individual`, sans SIRET (M10).
 * Les propriétaires et occupants rencontrés sur le terrain ne sont pas des clients : ce sont des
 * `Person` rattachées au site (M15).
 *
 * ⚠️ **`contactId` et `addressId` sont nullables, là où le diagramme ne le dit pas.** Un client
 * naît de l'import d'un fichier de demandes, qui en donne la raison sociale et la catégorie —
 * rarement une personne référente, jamais une adresse postale. Les exiger ferait refuser l'import
 * entier pour une colonne absente, ce que M21 interdit. Même arbitrage que `Organization.name`.
 */
export const clientSchema = z.object({
    id: uuidV7Schema,
    name: z.string().trim().min(1),
    category: clientCategorySchema,
    contactId: uuidV7Schema.nullable(),
    addressId: uuidV7Schema.nullable(),
    /** L'année d'entrée en relation — « Client depuis 2019 ». Une année, pas une date. */
    clientSince: z.number().int().nullable(),
    /**
     * Le SIRET, **sans contrôle de format** et absent chez un particulier. Même raison que celui de
     * l'organisation : c'est un champ d'en-tête de document, pas une clé.
     */
    siret: z.string().trim().min(1).nullable(),
    /**
     * Quand le client a été **retiré du répertoire actif** (`POST /clients/:id/archive`), `null`
     * tant qu'il y est. Sur le fil, une chaîne ISO — `JSON.stringify` d'une `Date` en rend une.
     */
    archivedAt: z.iso.datetime().nullable(),
});
/**
 * Les catégories qu'on peut **archiver** — le particulier, et lui seul, comme au prototype validé
 * (« Archiver ce particulier », `ghp-bo/screens.js:1752`).
 *
 * ⚠️ **Pourquoi pas les autres** : une commune, un bailleur ou un syndic porte des lots et des
 * campagnes qui durent, et le retirer du répertoire ferait disparaître des sélecteurs le donneur
 * d'ordre de demandes encore vivantes. Un particulier, lui, vient pour une vente et s'en va.
 */
export const ARCHIVABLE_CLIENT_CATEGORIES = [
    'individual',
];
export function isArchivableCategory(category) {
    return ARCHIVABLE_CLIENT_CATEGORIES.includes(category);
}
/**
 * Ce que le répertoire montre de l'archivage : les clients actifs (`false`, le défaut), les seuls
 * archivés (`true`), ou tous (`all`). Des chaînes parce que la valeur voyage dans l'URL.
 */
export const CLIENT_ARCHIVE_FILTERS = ['false', 'true', 'all'];
export const clientArchiveFilterSchema = z.enum(CLIENT_ARCHIVE_FILTERS);
/**
 * Les **trois familles d'affichage** du répertoire — le regroupement que les écrans appliquent
 * aux cinq catégories (glossaire de `docs/MODELE-DONNEES.md`). `private_professional` est
 * **dérivé, jamais stocké** : il n'est pas une sixième valeur de `Client.category`.
 *
 * L'ordre est celui du bandeau segmenté de la maquette : Communes, puis « Promoteurs, syndics &
 * bailleurs », puis Particuliers.
 */
export const CLIENT_FAMILIES = ['municipality', 'private_professional', 'individual'];
export const clientFamilySchema = z.enum(CLIENT_FAMILIES);
/**
 * La partition, écrite **une seule fois** : c'est elle que lisent le `WHERE` du dépôt, les trois
 * compteurs d'onglets et la pastille de la ligne. Trois écritures séparées finiraient par ranger
 * `building_manager` d'un côté pour le filtre et de l'autre pour le compteur — l'onglet
 * annoncerait alors un effectif que la liste ne montre pas.
 *
 * ⚠️ **Écrite dans ce sens-ci, et c'est ce qui la rend totale.** Un `Record<ClientCategory, …>`
 * exige ses cinq clés : ajouter une catégorie à `CLIENT_CATEGORIES` sans lui donner de famille
 * **ne compile pas**. La table inverse — famille → catégories — ne peut pas dire cela : rien n'y
 * oblige à mentionner les cinq, `satisfies` ne vérifiant que la validité de ce qui est écrit, pas
 * sa complétude. L'oubli n'y était rattrapé que par un spec, et en attendant `familyOf` devait
 * **deviner** une famille que le filtre et les compteurs, eux, n'auraient pas retenue : deux
 * vérités pour une seule partition. Le sens choisi supprime le cas plutôt que de le replier.
 */
const CATEGORY_FAMILY = {
    municipality: 'municipality',
    developer: 'private_professional',
    social_landlord: 'private_professional',
    building_manager: 'private_professional',
    individual: 'individual',
};
/** Les catégories d'une famille, lues dans la table ci-dessus — jamais recopiées à côté d'elle. */
function categoriesOf(family) {
    return CLIENT_CATEGORIES.filter((category) => CATEGORY_FAMILY[category] === family);
}
/**
 * L'inverse, **dérivé** et calculé une fois : le `WHERE` du dépôt le lit à chaque requête. Le
 * `Record<ClientFamily, …>` exige les trois familles, comme la table le fait des cinq catégories.
 */
const FAMILY_CATEGORIES = {
    municipality: categoriesOf('municipality'),
    private_professional: categoriesOf('private_professional'),
    individual: categoriesOf('individual'),
};
/** Les catégories qu'une famille recouvre — ce que le filtre traduit en `IN (…)`. */
export function categoriesInFamily(family) {
    return FAMILY_CATEGORIES[family];
}
/**
 * La famille d'une catégorie — **la lecture directe de la table**, donc la même source que le
 * filtre et les compteurs : la pastille « Privé » / « Pro » de la ligne et l'onglet qui la
 * contient ne peuvent pas se contredire.
 *
 * Plus de repli : il n'y a plus de cas à replier, le type garantissant une famille à chaque
 * catégorie. Rien ne peut donc échouer pendant un rendu (M21).
 */
export function familyOf(category) {
    return CATEGORY_FAMILY[category];
}
/**
 * Une ligne du répertoire « Clients & lots », telle que l'API la sert au back-office.
 *
 * ⚠️ **Ce que cette ligne ne porte pas, l'API ne le sert pas encore.** Le sous-titre d'un
 * particulier est, dans la maquette, le **motif** de sa demande — `Inquiry.purpose`, qui a sa
 * colonne depuis M32 sans qu'aucune requête la remonte. Le déclarer ici pour le servir en `null`
 * constant ferait croire à un câblage ; l'écran affiche donc la catégorie pour tout le monde.
 *
 * ⚠️ **L'avancement se calcule, il ne se stocke pas.** `inquiries` est compté en base à chaque
 * lecture, sur la chaîne `Client → Lot → Site → Inquiry` : un compteur stocké divergerait au
 * premier changement de statut, et personne ne saurait lequel des deux croire.
 */
export const clientListRowSchema = z.object({
    id: uuidV7Schema,
    name: z.string(),
    category: clientCategorySchema,
    /** Combien de lots ce client porte — la colonne « Lots ». */
    lotCount: z.number().int().min(0),
    inquiries: z.object({
        /**
         * Les demandes du client **que la barre montre** — le `Y` de « X / Y validés » : toutes sauf
         * les abandonnées, qui n'ont pas d'étape (`lotPipelineTotal`, le dénominateur de la fiche).
         */
        total: z.number().int().min(0),
        /**
         * Celles qui sont validées **ou archivées** — le `X`, et ce que la barre d'avancement remplit :
         * le segment « Validé » de la fiche (`LOT_PIPELINE_PARTITION`), qu'archiver ne vide pas.
         */
        validated: z.number().int().min(0),
    }),
    /**
     * `null` pour un client actif — le seul cas du répertoire par défaut (`archived=false`) ; c'est
     * sous `archived=all` qu'il distingue les lignes.
     */
    archivedAt: z.iso.datetime().nullable(),
});
/**
 * Les deux ordres du répertoire. `city` est la vue « Par ville » du bandeau segmenté : ce n'est
 * pas un filtre mais un **tri** — le répertoire entier, rangé par la ville de ses lots plutôt que
 * par la raison sociale. D'où sa place ici et non dans `family`.
 *
 * ⚠️ La ville vient de `Lot.city`, dénormalisée exprès par le diagramme pour éviter une jointure
 * vers `Site`/`Address` à chaque affichage.
 *
 * L'énumération est fermée pour la même raison que celle des demandes : la valeur finit en
 * `ORDER BY`, et une chaîne libre y serait une injection.
 */
export const CLIENT_LIST_SORTS = ['name', 'city'];
export const clientListSortSchema = z.enum(CLIENT_LIST_SORTS);
export const CLIENT_LIST_DIRECTIONS = ['asc', 'desc'];
export const clientListDirectionSchema = z.enum(CLIENT_LIST_DIRECTIONS);
/**
 * Ce que le serveur accepte pour construire une page du répertoire : la **seule** définition dont
 * dérivent l'URL de l'écran, le `WHERE` du dépôt et l'appel du client typé (D24).
 *
 * ⚠️ Ce schéma est **strict** : il décrit ce que le service reçoit, pas ce qu'une URL contient.
 * La tolérance M21 — une valeur illisible retombe sur son défaut plutôt que de rendre `400` —
 * vit à la frontière HTTP (`apps/api/src/clients/client-list-query.ts`).
 *
 * `family` accepte `'all'` plutôt que d'être facultatif : « toutes familles » est une valeur du
 * filtre, elle s'écrit dans l'URL et se lit dans un journal.
 */
export const clientListFilterSchema = z.object({
    /** Recherche libre — raison sociale et ville des lots. Vide = pas de recherche. */
    search: z.string(),
    family: z.union([z.literal('all'), clientFamilySchema]),
    /**
     * Actifs, archivés ou tous. ⚠️ **Il borne aussi les compteurs d'onglets**, contrairement à
     * `family` : un onglet « Particuliers » qui compterait les archivés annoncerait des lignes que
     * la liste ne montre pas.
     */
    archived: clientArchiveFilterSchema,
    sort: clientListSortSchema,
    direction: clientListDirectionSchema,
    limit: z.number().int().positive(),
    offset: z.number().int().min(0),
});
/**
 * Les effectifs d'onglets, comptés sur l'ensemble **recherché** mais sans le préréglage de
 * l'onglet (D24) : un compteur répond « combien si je clique ici », il ne peut donc pas porter la
 * famille qu'il annonce.
 *
 * ⚠️ **Les clés sont les valeurs de `ClientFamily`**, et ce n'est pas une coquetterie : l'écran
 * lit `counts[view]` sans table de correspondance, donc rien ne peut dériver entre le nom de
 * l'onglet et le nombre qu'il affiche. C'est la seule entorse à la casse `camelCase` du dépôt, et
 * elle est portée par une valeur d'énumération, pas par un identifiant inventé ici.
 *
 * ⚠️ « Par ville » n'y figure pas — c'est un tri, pas un sous-ensemble : son compteur serait
 * toujours celui de « Tous », ce qui ferait croire à un filtre inopérant.
 */
export const clientListCountsSchema = z.object({
    all: z.number().int().min(0),
    municipality: z.number().int().min(0),
    private_professional: z.number().int().min(0),
    individual: z.number().int().min(0),
});
/**
 * L'enveloppe paginée du répertoire (D24) : la tranche demandée, l'effectif de l'ensemble filtré,
 * et les compteurs d'onglets.
 *
 * `total` porte le préréglage de l'onglet, `counts` l'ignore : ils coïncident sur « Tous » et
 * divergent ailleurs, d'où les deux.
 */
export const clientListPageSchema = z.object({
    items: z.array(clientListRowSchema),
    total: z.number().int().min(0),
    counts: clientListCountsSchema,
});
/**
 * L'effectif **onglet compris**, déduit des compteurs plutôt que demandé à la base : les trois
 * familles partitionnent l'ensemble recherché, donc le chiffre est déjà connu. Une requête de
 * plus coûterait un balayage pour rien.
 */
export function clientListTotal(counts, family) {
    return family === 'all' ? counts.all : counts[family];
}
//# sourceMappingURL=client.js.map